import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { X, Cookie, Settings } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'agencia_ppc_cookie_consent';

const CookieBanner = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Translations
  const texts = {
    es: {
      title: 'Utilizamos cookies',
      description: 'Usamos cookies para mejorar tu experiencia en nuestro sitio web y analizar el tráfico.',
      necessary: 'Cookies necesarias',
      necessaryDesc: 'Esenciales para el funcionamiento del sitio.',
      analytics: 'Cookies analíticas',
      analyticsDesc: 'Nos ayudan a entender cómo usas el sitio.',
      acceptAll: 'Aceptar todas',
      acceptNecessary: 'Solo necesarias',
      moreInfo: 'Más información',
      privacyLink: 'Política de Cookies'
    },
    fr: {
      title: 'Nous utilisons des cookies',
      description: 'Nous utilisons des cookies pour améliorer votre expérience sur notre site et analyser le trafic.',
      necessary: 'Cookies nécessaires',
      necessaryDesc: 'Essentiels au fonctionnement du site.',
      analytics: 'Cookies analytiques',
      analyticsDesc: 'Nous aident à comprendre comment vous utilisez le site.',
      acceptAll: 'Tout accepter',
      acceptNecessary: 'Nécessaires uniquement',
      moreInfo: 'Plus d\'informations',
      privacyLink: 'Politique de Cookies'
    },
    en: {
      title: 'We use cookies',
      description: 'We use cookies to improve your experience on our website and analyze traffic.',
      necessary: 'Necessary cookies',
      necessaryDesc: 'Essential for the website to function.',
      analytics: 'Analytics cookies',
      analyticsDesc: 'Help us understand how you use the site.',
      acceptAll: 'Accept all',
      acceptNecessary: 'Necessary only',
      moreInfo: 'More information',
      privacyLink: 'Cookie Policy'
    }
  };

  const t = texts[language] || texts.es;

  const cookiesLink = {
    es: '/es/cookies',
    fr: '/fr/cookies',
    en: '/en/cookies'
  };

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      necessary: true,
      analytics: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
    // Dispatch event to trigger GTM loading
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      necessary: true,
      analytics: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
    // Dispatch event (GTM won't load since analytics is false)
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-slate-200 shadow-2xl animate-in slide-in-from-bottom duration-500"
      data-testid="cookie-banner"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Content */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Cookie className="w-5 h-5 text-yellow-500" />
              <h3 className="font-semibold text-slate-800">{t.title}</h3>
            </div>
            <p className="text-sm text-slate-600 mb-2">
              {t.description}{' '}
              <a 
                href={cookiesLink[language] || cookiesLink.es}
                className="text-blue-600 hover:underline"
              >
                {t.privacyLink}
              </a>
            </p>

            {/* Details toggle */}
            {showDetails && (
              <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm space-y-2 animate-in fade-in duration-300">
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 mt-1 rounded-full bg-green-500 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium text-slate-700">{t.necessary}</span>
                    <p className="text-slate-500 text-xs">{t.necessaryDesc}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 mt-1 rounded-full bg-blue-500 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium text-slate-700">{t.analytics}</span>
                    <p className="text-slate-500 text-xs">{t.analyticsDesc}</p>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-xs text-slate-500 hover:text-slate-700 mt-2 flex items-center gap-1"
            >
              <Settings className="w-3 h-3" />
              {t.moreInfo}
            </button>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 lg:flex-shrink-0">
            <Button
              onClick={handleAcceptNecessary}
              variant="outline"
              className="text-sm border-slate-300 hover:bg-slate-100"
              data-testid="cookie-accept-necessary"
            >
              {t.acceptNecessary}
            </Button>
            <Button
              onClick={handleAcceptAll}
              className="text-sm bg-green-500 hover:bg-green-600 text-white"
              data-testid="cookie-accept-all"
            >
              {t.acceptAll}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
